/**
 * The panel four configuration screens render — Task U14 (Tier-3), KTD13.
 *
 * `useVersionedConfig` decides the lifecycle; this decides what each of its states
 * looks like, so U15–U18 inherit one answer instead of writing four. It renders the
 * versions, the active marker, the diff of the draft against the active version, and
 * the conflict — and, in the three states screens usually forget, the skeleton, the
 * empty configuration type and the failed read.
 *
 * ── It is not generic, on purpose ───────────────────────────────────────────
 *
 * The hook has already projected every version into a `VersionedConfigRow` — label,
 * active marker, rendered settings, precondition — so nothing here knows what a
 * policy or a formula is. Every prop this component shares with the hook's state is
 * spelled **the same**, so a screen really does write
 * `<VersionedConfigPanel {...state} title={…} subject={…} emptyTitle={…} />` and add
 * only its own words and its own controls. Fix round 1 renamed `onReload` and
 * `onAcknowledgeOverwrite` for that: the report claimed the spread worked while the
 * two names did not match, and the suite's own harness was passing ten props by hand
 * — which is what U15–U18 would then have done four more times.
 *
 * ── The write states are here too ───────────────────────────────────────────
 *
 * `writeError`, `pending` and `saved`, added in fix round 1. A `403`, a `422` or a
 * `429` on create, patch or activate was invisible through this component, so each of
 * U15–U18 would have written its own refusal note, its own busy line and its own
 * saved confirmation — three more states, four times over, which is exactly the drift
 * KTD13 exists to stop. The refusal renders through `ApiErrorNote` with **no**
 * `onRetry`: a write is not a read, and re-pressing a button that was refused is the
 * caller's decision, not a control this panel should offer.
 *
 * They sit **outside the phase branches**, beside the conflict note, and fix round 2
 * is why: they were inside the `ready` fragment while the create action lives in the
 * `EmptyState`, so the first write on each of the four screens happened in a phase
 * where a `403` was invisible — and a `409` on the same press was not, because the
 * conflict note was already outside. A conflict is also the `writeError`, so exactly
 * one of the two notes renders for it: the conflict's, which is the richer.
 *
 * ── The words belong to the screen ──────────────────────────────────────────
 *
 * `title`, `subject`, `emptyTitle` and `emptyDescription` are required of the caller
 * rather than defaulted here, which is `EmptyState`'s own rule and for its own
 * reason: "no versions yet" and "no alert rules configured for this company yet" are
 * different facts, and one sentence written for four screens erases the difference.
 * What this file owns is the layout and the guarantees — the conflict always names
 * the newer token, always says the draft was kept, and always shows the stored value
 * beside the typed one or says plainly that it has not been read back yet.
 *
 * ── Retry prints the server's sentence ──────────────────────────────────────
 *
 * Through `ApiErrorNote`, which is this codebase's single implementation of
 * `API_CONTRACT.md` §5: branch on `code`, never on `message`, and show the server's
 * own sentence exactly where §5.1 marks it safe. A paraphrase written here would be
 * a fifth opinion about an error nine screens already agree about.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import type { WorkforceApiError } from '../api/errors';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Badge } from '../states/Badge';
import { EmptyState } from '../states/EmptyState';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import type {
  VersionedConfigConflict,
  VersionedConfigDiffEntry,
  VersionedConfigPhase,
  VersionedConfigRow,
} from './useVersionedConfig';
import { ErrorCodeLine } from '../states/ErrorCodeLine';

export interface VersionedConfigPanelProps {
  readonly phase: VersionedConfigPhase;
  readonly rows: readonly VersionedConfigRow[];
  readonly diff: readonly VersionedConfigDiffEntry[];
  readonly conflict: VersionedConfigConflict | null;
  /** The read's refusal, rendered in `retry`. */
  readonly error: WorkforceApiError | null;
  /** The last write's refusal, conflict or not. Rendered beside the versions. */
  readonly writeError: WorkforceApiError | null;
  /** A write is in flight. */
  readonly pending: boolean;
  /** The last write landed and nothing has been attempted since. */
  readonly saved: boolean;
  /** Re-reads the list — the retry action, and what loads a conflict's stored value. */
  readonly reload: () => void;
  readonly acknowledgeOverwrite: (value: boolean) => void;
  /** The heading. */
  readonly title: string;
  /** What could not be read, in words — "quy tắc cảnh báo". */
  readonly subject: string;
  readonly emptyTitle: string;
  readonly emptyDescription?: string;
  /** The create control. Rendered inside `empty`, and above the table when ready. */
  readonly createAction?: ReactNode;
  /**
   * Per-row write controls — activate, edit.
   *
   * The screen owns them; the hook gates the **request**, never the control, so this
   * panel is what keeps the two in step: it calls `rowAction` only for a row with
   * `writable === true`. A control on a row the hook will refuse would be present,
   * enabled and inert, which is worse than either absent or disabled.
   */
  readonly rowAction?: (row: VersionedConfigRow) => ReactNode;
  /**
   * The words of the marker every row that is NOT the active one wears. Defaults to
   * `config.inactive`. Roadmap E0-4: payroll parameters form a timeline on which several
   * versions are activated at once, and the one active marker names the version in force
   * today — so "not yet applied" would be false of an earlier stretch that did apply.
   * The surface says what its marker means instead.
   */
  readonly inactiveLabel?: string;
  /**
   * Facts about one version the hook's row does not carry, rendered in the state cell
   * under the marker — never instead of it. Roadmap E0-4: a payroll version's own
   * lifecycle, its confirmation and its author. The words are the screen's; the cell is
   * here.
   */
  readonly rowState?: (row: VersionedConfigRow) => ReactNode;
  readonly testId?: string;
}

/** `retention_days: 180 · enabled: true` — the settings of one version, in words. */
function describeFields(fields: Readonly<Record<string, string>>): string {
  return Object.entries(fields)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ');
}

export function VersionedConfigPanel(props: VersionedConfigPanelProps): ReactNode {
  const { t } = useI18n();
  const testId = props.testId ?? 'u14-config';
  const { conflict } = props;

  /*
    A version conflict is BOTH `conflict` and `writeError` — the hook's `writeError`
    is "the last write's refusal, conflict or not" — so rendering both put the same
    sentence and the same code on screen twice, once as a high-severity alert and
    once as a medium-severity note. The conflict note is the richer of the two (it
    names the newer token, says the draft was kept, and carries the overwrite gate),
    so it wins and the plain note stands down. Identity, not code: two separate
    refusals that happen to share a code are two events.
  */
  const writeRefusal =
    props.writeError !== null && props.writeError === conflict?.error ? null : props.writeError;

  return (
    <section className="wp-section" aria-labelledby={`${testId}-heading`} data-testid={testId}>
      <h2 className="wp-section__title" id={`${testId}-heading`}>
        {props.title}
      </h2>

      {props.phase === 'skeleton' ? (
        <Skeleton rows={4} variant="block" label={props.subject} testId={`${testId}-skeleton`} />
      ) : null}

      {props.phase === 'retry' && props.error !== null ? (
        <ApiErrorNote
          error={props.error}
          onRetry={props.reload}
          subject={props.subject}
          testId={`${testId}-error`}
        />
      ) : null}

      {props.phase === 'empty' ? (
        <EmptyState
          title={props.emptyTitle}
          {...(props.emptyDescription === undefined ? {} : { description: props.emptyDescription })}
          {...(props.createAction === undefined ? {} : { action: props.createAction })}
          testId={`${testId}-empty`}
        />
      ) : null}

      {conflict === null ? null : (
        <StatusNote
          severity="high"
          role="alert"
          title={t('config.conflict.title')}
          testId={`${testId}-conflict`}
        >
          <p data-testid={`${testId}-conflict-token`}>
            {conflict.storedToken === null
              ? t('config.conflict.token.unknown')
              : t('config.conflict.token', { token: conflict.storedToken })}
          </p>
          <p>{t('config.conflict.kept')}</p>
          {/*
            The server's own sentence, plus the correlation id `ApiErrorNote` puts on
            screen in every other refusal "because it is the only thing a person can
            quote that lets support find the request". `VersionedConfigConflict.error`
            says it is carried for this; fix round 1 made that true.
          */}
          <p className="wp-small">
            {conflict.error.userSafe ? conflict.error.message : t('common.unknownError')}
          </p>
          <ErrorCodeLine code={conflict.error.code} correlationId={conflict.error.correlationId} />

          {/*
            The stored value beside the typed one, which is what U11's review round 1
            forced onto `RetentionScreen`: after a refused write the field still shows
            the person's own number, so without this the value they would be
            overwriting is never on screen. `null` is said out loud rather than
            filled in from the row — the row still holds what they read, not what won.
          */}
          {props.diff.length === 0 ? null : (
            <ul data-testid={`${testId}-conflict-compare`}>
              {props.diff.map((entry) => (
                <li key={entry.key}>
                  {t('config.conflict.compare', {
                    key: entry.key,
                    stored: conflict.stored?.[entry.key] ?? t('config.conflict.stored.unknown'),
                    yours: entry.to,
                  })}
                </li>
              ))}
            </ul>
          )}

          {conflict.stored === null ? (
            <p>
              <button
                type="button"
                className="wp-button"
                data-testid={`${testId}-conflict-load`}
                onClick={props.reload}
              >
                {t('config.conflict.loadStored')}
              </button>
            </p>
          ) : null}

          <label className="wp-toggle">
            <input
              type="checkbox"
              data-testid={`${testId}-conflict-overwrite`}
              checked={conflict.acknowledged}
              onChange={(event) => {
                props.acknowledgeOverwrite(event.target.checked);
              }}
            />
            <span className="wp-small">{t('config.conflict.overwrite')}</span>
          </label>
        </StatusNote>
      )}

      {/*
        Outside the phase branches, beside the conflict note, and fix round 2 is about
        why. These three used to live inside the `ready` fragment — but the create
        action lives in the `EmptyState` above, so the **first** write on any of the
        four screens happens in `empty`: U18's first-run company arriving there (through
        `emptyOnRefusal` until roadmap E0-4, through an empty history since), or the
        first policy, rule or formula anybody writes. A `403`, a `422` or a `429` on
        that write was silently invisible and the busy line never appeared, while a
        `409` on the very same press was visible, because the conflict note was already
        outside the branches. Two refusals of one button, one of them shown.
      */}
      {writeRefusal === null ? null : (
        <ApiErrorNote
          error={writeRefusal}
          subject={props.subject}
          testId={`${testId}-write-error`}
        />
      )}

      {props.pending ? (
        <p className="wp-small wp-muted" role="status" data-testid={`${testId}-pending`}>
          {t('config.pending')}
        </p>
      ) : null}

      {props.saved && !props.pending ? (
        <StatusNote severity="low" title={t('config.saved')} testId={`${testId}-saved`} />
      ) : null}

      {props.phase === 'ready' ? (
        <>
          {props.createAction === undefined ? null : (
            <div className="wp-filters__actions">{props.createAction}</div>
          )}

          <div className="wp-table-scroll">
            <table className="wp-table" data-testid={`${testId}-table`}>
              <caption className="wp-visually-hidden">{t('config.table.caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('config.column.version')}</th>
                  <th scope="col">{t('config.column.state')}</th>
                  <th scope="col">{t('config.column.settings')}</th>
                  {props.rowAction === undefined ? null : (
                    <th scope="col">{t('config.column.actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {props.rows.map((row) => (
                  <tr key={row.id} data-testid={`${testId}-row-${row.id}`}>
                    <th scope="row">
                      <span>{row.label}</span>
                      <br />
                      <span className="wp-small wp-muted" data-testid={`${testId}-token-${row.id}`}>
                        {row.precondition === null
                          ? t('config.token.none')
                          : row.tokenLabel === null
                            ? t('config.token.unversioned')
                            : t('config.token', { token: row.tokenLabel })}
                      </span>
                    </th>
                    <td>
                      {row.active ? (
                        <Badge
                          severity="low"
                          icon="circle-check"
                          label={t('config.active')}
                          testId={`${testId}-active-${row.id}`}
                        />
                      ) : (
                        <span className="wp-small wp-muted">
                          {props.inactiveLabel ?? t('config.inactive')}
                        </span>
                      )}
                      {props.rowState === undefined ? null : (
                        <div className="wp-small" data-testid={`${testId}-state-${row.id}`}>
                          {props.rowState(row)}
                        </div>
                      )}
                    </td>
                    <td className="wp-small" data-testid={`${testId}-fields-${row.id}`}>
                      {describeFields(row.fields)}
                    </td>
                    {props.rowAction === undefined ? null : (
                      <td>{row.writable ? props.rowAction(row) : null}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div data-testid={`${testId}-diff`}>
            <h3 className="wp-small">{t('config.diff.title')}</h3>
            {props.diff.length === 0 ? (
              <p className="wp-small wp-muted">{t('config.diff.empty')}</p>
            ) : (
              <ul>
                {props.diff.map((entry) => (
                  <li
                    key={entry.key}
                    className="wp-small"
                    data-testid={`${testId}-diff-${entry.key}`}
                  >
                    {t('config.diff.change', {
                      key: entry.key,
                      from: entry.from ?? t('config.diff.absent'),
                      to: entry.to,
                    })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
